'use client'
import React, { useState, useContext, useEffect } from 'react'
import Router, { useRouter } from 'next/router'
import Link from 'next/link'
import { isBefore } from 'date-fns'
import { Button, Input, Spinner } from "@heroui/react"

import Purchase from './purchase'
import JoinButton from './join-button'
import StatusChip from './status-chip'
import PriceCard from './price-card'

import { jsonGetFetcher } from '../src/fetchers'
import localStorage from '../src/local-storage'
import { formatDate, formatMoneyIntl, getCurrencySymbol } from '../src/formatter'
import { logEvent } from '../src/analytics'
import useOrganization from '../hooks/use-organization'
import { UserContext } from '../contexts/user-context'
import { inputStyle } from '../src/styles'

const JoinContainer = ({ user, challenge, joinNotifier, coupons }) => {
  const [errorMsg, setErrorMsg] = useState('')
  const [isJoining, setIsJoining] = useState('')
  const [validatingCoupon, setValidatingCoupon] = useState(false)
  const [errorCoupon, setErrorCoupon] = useState('')
  const [coupon, setCoupon] = useState('')
  const [entryFee, setEntryFee] = useState(challenge.entryFee || '')
  const [errorEntryFee, setErrorEntryFee] = useState('')
  const [paymentToken, setPaymentToken] = useState('')
  const userHook = useContext(UserContext)
  const orgHook = useOrganization(user, challenge.group.organizationId)
  
  // coupons are loaded on server-side request on join page
  const hasCoupons = Boolean(coupons?.count > 0)

  const router = useRouter()
  const { code: passThroughCode, autoJoin } = router.query

  let joinDeadlinePassed = false
  if (challenge && challenge.joinDeadline) {
    joinDeadlinePassed = isBefore(new Date(challenge.joinDeadline), new Date())
  }

  useEffect(() => {
    if (router.isReady && autoJoin && !challenge.entryFee && !challenge.over && !joinDeadlinePassed) {
      joinFn(passThroughCode)
    }
  }, [router])

  const toSignIn = () => {
    let redirect = challenge.group.type === 'event' ? `/group/${challenge.group.id}` : `/join/${challenge.id}`
    if (passThroughCode) redirect += `?code=${passThroughCode}`
    localStorage.setRedirect(redirect)
    Router.push(`/signup`)
  }

  const joinError = async (err) => {
    console.error(err)
    setIsJoining(false)
    if (err.match(/challenge code is invalid/)) {
      setErrorMsg('Invalid code. Please contact the challenge administrator for the valid CODE.')
    } else if (err.match(/not enough credits/)) {
      const group = await jsonGetFetcher([`${process.env.API}/groups/${challenge.groupId}`])
      setErrorMsg(`Unfortunately this challenge does not have enough participant credits. Please contact ${group.organization.name}, the challenge administrator, and request that they add participant credits so that you can join.`)
    } else {
      setErrorMsg('There was a problem joining this challenge. Contact Challenge Hound support.')
    }
  }

  const joinFn = async (code, paymentToken, couponId) => {
    setIsJoining(true)
    try {
      const postConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.token
        },
        body: JSON.stringify({ challengeId: challenge.id, groupId: challenge.group.id, code: code || passThroughCode, token: paymentToken, couponId })
      }
      const res = await fetch(`${process.env.API}/challenges/join`, postConfig)
      if (!res.ok) {
        const errMsg = await res.text()
        await joinError(errMsg)
      } else {
        // add joined to window object, so confirm message is displayed on challenge page
        if (!window.joined) window.joined = {}
        // window.scrollTo(0, 0)
        
        // update user object to include newly joined challenge
        await userHook.mutate()
        logEvent(`challenge-joined`, 1)

        if (challenge.group.type === 'event') {
          window.joined[challenge.group.id] = true
          Router.push(`/group/[id]`, `/group/${group.id}`)
        } else {
          window.joined[challenge.id] = true
          if (joinNotifier) joinNotifier()
          Router.push(`/challenge/[id]`, `/challenge/${challenge.id}`)
        }
      }
    } catch (err) {
      console.error(err)
      setIsJoining(false)
      const msg = 'There was a problem joining this challenge. Contact Challenge Hound support.'
      if (couponId) {
        setErrorCoupon(msg)
      } else {
        setErrorMsg(msg)
      }
    }
  }

  const applyCoupon = async () => {
    if (!coupon) {
      setErrorCoupon('Invalid Entry Code')
      return
    }
    if (coupon.trim().match(/\s/)) {
      setErrorCoupon('Invalid Entry Code')
      return
    }

    setValidatingCoupon(true)
    try {
      const postConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.token
        },
        body: JSON.stringify({ coupon })
      }
      const res = await fetch(`${process.env.API}/coupons/${challenge.group.organizationId}/${challenge.id}`, postConfig)
      if (!res.ok) {
        const errMsg = await res.text()
        setErrorCoupon(errMsg)
        return
      }
      const data = await res.json()
      if ( !data.isValid ) {
        setValidatingCoupon(false)
        setErrorCoupon('Invalid Entry Code')
        return
      }
      
      setValidatingCoupon(false)
      await joinFn(undefined, undefined, data.couponId)
      
    } catch (err) {
      console.error(err)
      setValidatingCoupon(false)
      setErrorCoupon('There was a problem validating the entry code.')
    }
    
  }

  // PARENT CHALLENGE WITH NO JOINING
  if (challenge && challenge.childGroupId) {
    return (
      <div className='text-gray-400 mt-4 text-center'>
        You can join this challenge automatically by joining one of the teams or challenges listed <Link className='text-info' href={`/group/[id]`} as={`/group/${challenge.childGroupId}`}>here</Link>.
      </div>
    );
  }

  // DEADLINE PASSED
  if (joinDeadlinePassed && !challenge.over) {
    return <div className='flex flex-col justify-center'>
      <div className='flex justify-center'>
        <Button
          size='lg'
          type='submit'
          disabled={true}>DEADLINE TO JOIN HAS PASSED</Button>
      </div>
      <div className='flex justify-center text-gray-400 mt-4'>Deadline to join was: {formatDate(challenge.joinDeadline, true)}</div>
    </div>
  }

  // CHALLENGE OVER
  if (challenge.over) {
    return <Button
      size='lg'
      isDisabled={true}>CHALLENGE OVER</Button>
  }

  // NO USER, NEEDS TO SIGN IN
  if (!user || !user.id) {
    return <div className='flex justify-center'>
      <Button
        color='primary'
        size='lg'
        onClick={toSignIn}>
          {`SIGN UP TO JOIN ${challenge.group.type.toUpperCase()}`}
      </Button>
    </div>
  }

  // ALREADY JOINED, SHOW TO CHALLENGE BUTTON
  if (!isJoining && user.challengesJoined && user.challengesJoined.find(c => { return c.id === challenge.id && (c.joined || c.whitelisted) })) {
    return <div className='flex justify-center'>
      <Button
        size='lg'
        onPress={() => {
          Router.push(`/challenge/[id]`, `/challenge/${challenge.id}`)
        }}> 
        ALREADY JOINED, TO DASHBOARD
      </Button>
    </div> 
  }

  if (challenge.entryFee) {
    return (
      <div className='max-w-[550px] border-gray-300 border-1 rounded-md mx-auto p-4'>
        
        <div className='text-xl mb-4 text-center'>Pay Entry Fee</div>
        
        {!challenge.entryFeeVariable && <PriceCard a={formatMoneyIntl(challenge.entryFee / 100, orgHook.organization.currency || 'usd')} b='Entry Fee' />}
        {challenge.entryFeeVariable && <div>
          <div className='grid grid-cols-4 gap-2 mb-4'>
            <div className='flex justify-center'><Button color='primary' variant={entryFee === challenge.entryFee * 2 ? 'solid' : 'ghost'} onPress={() => { setEntryFee(challenge.entryFee * 2) }}>{formatMoneyIntl((challenge.entryFee * 2) / 100, orgHook.organization.currency || 'usd')}</Button></div>
            <div className='flex justify-center'><Button color='primary' variant={entryFee === challenge.entryFee * 4 ? 'solid' : 'ghost'} onPress={() => { setEntryFee(challenge.entryFee * 4) }}>{formatMoneyIntl((challenge.entryFee * 4) / 100, orgHook.organization.currency || 'usd')}</Button></div>
            <div className='flex justify-center'><Button color='primary' variant={entryFee === challenge.entryFee * 10 ? 'solid' : 'ghost'} onPress={() => { setEntryFee(challenge.entryFee * 10) }}>{formatMoneyIntl((challenge.entryFee * 10) / 100, orgHook.organization.currency || 'usd')}</Button></div>
            <div className='flex justify-center'><Button color='primary' variant={entryFee === challenge.entryFee * 20 ? 'solid' : 'ghost'} onPress={() => { setEntryFee(challenge.entryFee * 20) }}>{formatMoneyIntl((challenge.entryFee * 20) / 100, orgHook.organization.currency || 'usd')}</Button></div>
          </div>

          <Input
              label='Entry Fee Amount'
              type='number'
              name='entryFee'
              maxLength={20}
              placeholder={`Minimum ${formatMoneyIntl(challenge.entryFee / 100, orgHook.organization.currency || 'usd')}`}
              value={entryFee && !isNaN(entryFee) ? entryFee / 100 : ''}
              size='lg'
              radius='sm'
              errorMessage={errorEntryFee}
              isInvalid={Boolean(errorEntryFee) || (entryFee && !isNaN(entryFee) && entryFee < parseInt(challenge.entryFee))}
              classNames={ inputStyle }
              className='w-[200px] mx-auto'
              startContent={getCurrencySymbol(orgHook.organization.currency || 'usd')}
              onChange={(e) => {
                setErrorEntryFee('')
                let amt = parseFloat(e.target.value)
                if (!isNaN(amt)) {
                  if (amt * 100 < challenge.entryFee) {
                    amt = challenge.entryFee / 100
                  }
                  setEntryFee(amt * 100)
                }
              }}
            />
        </div>}

        <div className='mt-4 mb-4 text-gray-400'>
          This challenge was created by the <span className='text-primary font-bold'>{orgHook.organization.name.replace(/^the+/i, '')}</span> and requires a&nbsp;
          {challenge.entryFeeVariable && 'minimum '}
          <span className='text-primary font-bold'>{orgHook.organization.id && formatMoneyIntl(challenge.entryFee / 100, orgHook.organization.currency || 'usd')}</span> fee 
          to join. 
          {challenge.entryFeeVariable && ` Additional amounts help support the ${orgHook.organization.name.replace(/^the+/i, '').replace(/ organization$/i, '')} organization. `}
        </div>

        {hasCoupons && <div className='max-w-[350px] grid grid-cols-2 gap-2 mx-auto my-6'>
          <div>
            <Input
              label='Discount Code'
              autoComplete='off'
              autoCorrect='off'
              autoCapitalize="off"
              spellCheck='false'
              name='coupon'
              maxLength={20}
              placeholder=''
              value={coupon}
              size='md'
              radius='sm'
              errorMessage={errorCoupon}
              isInvalid={Boolean(errorCoupon)}
              classNames={ inputStyle }
              className=''
              onChange={(e) => {
                setErrorCoupon('')
                setCoupon(e.target.value)
              }}
            />
          </div>
          <div className='flex items-center'>
            <Button
              color='primary'
              size='md'
              className='min-w-[170px] py-6'
              isDisabled={validatingCoupon}
              onPress={applyCoupon}>{validatingCoupon ? <><Spinner color='white' />Validating...</> : 'Apply Discount Code'}</Button>
          </div>
        </div>}

        <StatusChip color='danger' msg={errorMsg} />

        {(orgHook.organization.id && !orgHook.organization.stripeAccountEnabled) && <div className='p-4 border-gray-300 border-1 font-italic rounded-md mt-8'>
          Payments cannot be processed until {orgHook.organization.name} completes the account set up.
        </div>}
        
        {orgHook.organization.stripeAccountEnabled && <div className='md:min-h-[309px] relative'>
          {isJoining && <>
            <div className='absolute h-full w-full flex justify-center bg-gray-200 z-40 opacity-80 rounded-md'></div>
            <div className='absolute h-full w-full flex justify-center z-50'>
              <Spinner size='lg' className='text-primary' />
              <div className='absolute top-20 text-xl font-bold text-black ml-2'>{paymentToken ? 'Payment Successful' : ''}<br/>Joining Challenge...</div>
            </div>
          </>}
          <Purchase user={user} type='entryFee' amount={entryFee} currency={orgHook.organization.currency || 'usd'} challengeId={challenge.id} quantity={1} cb={async (reset, result) => {
            setPaymentToken(result.id)
            await joinFn(undefined, result.id)
          }} />
        </div>}
        
      </div>
    );  
  
} else {

    return <div className='w-full flex flex-col items-center justify-center'>
      {(isJoining && autoJoin) && <div className='flex justify-center flex-col mb-4'>
        <Spinner color='secondary' />
        <div className='mt-2 text-xl font-bold text-primary ml-2'>Joining Challenge</div>
      </div>}
      <div><StatusChip color='danger' msg={errorMsg} /></div>
      {(!isJoining || !autoJoin) && <JoinButton 
        user={user} 
        challenge={challenge} 
        passThroughCode={passThroughCode}
        joinFn={joinFn}
        clearError={() => setErrorMsg('')} />}
    </div>
  
  }
}

export default JoinContainer